import axios from 'axios'

export const api = axios.create({
  baseURL: '',
  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * URL for opening a course file in a browser tab.
 *
 * The download route 302s to DO Spaces, which has no CORS headers — fetching it
 * via XHR fails silently. A plain tab follows the redirect fine, but sends no
 * Authorization header, so the token rides in the query string instead.
 */
export function courseFileUrl(fileId: number): string {
  const token = localStorage.getItem('access_token')
  const qs = token ? `?jwt=${encodeURIComponent(token)}` : ''
  return `/api/parent/files/${fileId}/download${qs}`
}

export function openCourseFile(fileId: number): void {
  window.open(courseFileUrl(fileId), '_blank')
}

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // If 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      const refreshToken = localStorage.getItem('refresh_token')
      if (refreshToken) {
        try {
          const response = await axios.post('/api/auth/refresh', {}, {
            headers: {
              Authorization: `Bearer ${refreshToken}`,
            },
          })

          const { access_token } = response.data
          localStorage.setItem('access_token', access_token)

          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${access_token}`
          return api(originalRequest)
        } catch {
          // Refresh failed, logout
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          window.location.href = '/login'
        }
      }
    }

    return Promise.reject(error)
  }
)
